import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LastWebsocketJsonMessageType,
  WebsocketAction,
} from '@interfaces/webSocketConnectionInfo';
import { useTrainerToolsContent as useTrainerTools } from '@features/TrainerTools/provider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { logSkippedSessionMessage } from '@helpers/sessionMessageSkipLogger';
import { Screens } from '@features/TrainerTools/interfaces';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { getNotification } from '@components/Notification/helpers';

const { Asset3DReady, Asset3DNewLimits, Asset3DNewFailed } = WebsocketAction;

const useHandleWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { gameliftStreamsSessionKey } = useMytaverseEvent();
  const { isDebugMode } = useMytaverse();

  const { t: translate } = useTranslation();

  const { showNotification } = useNotification();

  const {
    setActiveObjectReady,
    setHeightLimits,
    setSizeLimits,
    setActiveObject,
    setTemporaryObject,
    setActiveScreen,
    setIsExploration,
  } = useTrainerTools();

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, sessionKey, payload } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return logSkippedSessionMessage(sessionKey, gameliftStreamsSessionKey);
    }

    switch (action) {
      case Asset3DReady: {
        //TODO: check for it
        setActiveObjectReady(true);
        break;
      }

      case Asset3DNewLimits: {
        const { size, height } = payload;
        setHeightLimits({
          min: Math.floor(parseFloat(height.min) * 100) / 100,
          max: Math.floor(parseFloat(height.max) * 100) / 100,
        });
        setSizeLimits({
          min: Math.floor(parseFloat(size.min) * 100) / 100,
          max: Math.floor(parseFloat(size.max) * 100) / 100,
        });
        break;
      }

      case Asset3DNewFailed: {
        setActiveObject(null);
        setTemporaryObject(null);
        setActiveObjectReady(false);
        setIsExploration(false);

        setActiveScreen(Screens.COLLECTION);

        showNotification(
          getNotification({
            message: translate('notifications.assetsErrorLoad'),
            type: NOTIFICATION_TYPES.WARNING,
            withDismissButton: false,
            withReloadButton: false,
          }),
        );
        break;
      }

      default:
        break;
    }
  }, [lastJsonMessage, gameliftStreamsSessionKey, isDebugMode]);
};

export default useHandleWebsocketMsg;
